import { Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import ApplicationDecisionModal from 'components/applications/ApplicationDecisionModal';
import ApplicationQuestionsFormSection from 'components/applications/ApplicationQuestionsFormSection';
import Spinner from 'components/shared/Spinner';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import ApplicationsService, { ApplicationSubmitResponseData } from 'services/api/ApplicationsService';
import routes from 'store/configs/Routes';
import ApplicationAnswer, {
  ApplicationAnswerData,
  ApplicationFileAnswer,
  ApplicationUploadedFile,
  SublistAnswerData,
} from 'store/types/ApplicationAnswer';
import { ApplicationContentSectionView, ApplicationContentView } from 'store/types/ApplicationContent';
import ApplicationQuestion from 'store/types/ApplicationQuestion';
import {
  getAnswersData,
  getApplicationStatusAlert,
  getFormattedApplicationStatus,
  getFormattedSections,
  getQuestionFieldName,
  getQuestionId,
} from 'util/Application';
import ApplicationStatus from 'store/enums/ApplicationStatus';
import ApplicationPageLayout from 'components/applications/ApplicationPageLayout';
import useUnsavedChangesPrompt from 'hooks/useUnsavedChangesPrompt';
import { getUnsavedChangesPromptMessage } from 'util/Form';
import QuestionType from 'store/enums/QuestionType';
import ApplicationConfirmationModal from 'components/applications/ApplicationConfirmationModal';
import { defaultSnackbarErrorProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';
import pageStyles from '../ApplicationPage.module.scss';

interface EditApplicationPageViewProps {
  data: ApplicationContentView;
  isPrivate?: boolean;
  refetch: () => void;
}

const EditApplicationPageView: React.FunctionComponent<EditApplicationPageViewProps> = ({
  data,
  isPrivate = false,
  refetch,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { handleSubmit, formState, getValues, setError, watch } = useFormContext();
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const { removeUnsavedChangesPrompt, prompt } = useUnsavedChangesPrompt(getUnsavedChangesPromptMessage());
  const [decisionModalOpen, setDecisionModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<ApplicationSubmitResponseData | undefined>(undefined);
  const [sections, setSections] = useState<ApplicationContentSectionView[]>([]);
  const questionsList: ApplicationQuestion[] = useMemo(() => {
    let result: ApplicationQuestion[] = [];

    if (data) {
      (data.sections || []).forEach(({ questions = [] }) => {
        result = result.concat(questions);
      });
    }
    return result;
  }, [data]);
  const values = watch();
  const hasContent: boolean = useMemo(() => !!(data && data.sections && data.sections.length), [data]);
  const requiredCheckboxQuestions: string[] = useMemo(
    () =>
      questionsList
        .filter(({ required, type }) => required && type === QuestionType.Checkbox)
        .map(({ fieldName }) => fieldName),
    [questionsList]
  );

  const requiredSublistQuestions: string[] = useMemo(
    () =>
      questionsList
        .filter(({ required, type }) => required && (type === QuestionType.SubList || type === QuestionType.Address))
        .map(({ fieldName }) => fieldName),
    [questionsList]
  );
  const submitButtonDisabled: boolean = useMemo(
    () =>
      !hasContent ||
      loading ||
      !formState.isValid ||
      !requiredSublistQuestions.every((questionId) => !!values[questionId]?.length) ||
      !requiredCheckboxQuestions.every((questionId) => values[questionId].some(({ value }: any) => !!value)),
    [hasContent, loading, formState.isValid, requiredSublistQuestions, requiredCheckboxQuestions, values]
  );

  const handleDecisionModalOpen = useCallback(() => {
    setDecisionModalOpen(true);
  }, []);

  const handleDecisionModalClose = useCallback(() => {
    setDecisionModalOpen(false);
  }, []);

  useEffect(() => {
    if (data) {
      setSections(getFormattedSections(data.sections));
    }
  }, [data]);

  const uploadFiles = useCallback(
    (request, answersData: ApplicationAnswer[], files: ApplicationFileAnswer, sublistFiles?: ApplicationFileAnswer) => {
      const result: ApplicationAnswerData[] = answersData;
      const uploadPromiseArray: Promise<ApplicationUploadedFile>[] = [];

      if (files) {
        Object.entries(files).forEach(([questionId, files]) => {
          if (files?.length) {
            files.forEach((file: File) =>
              uploadPromiseArray.push(ApplicationsService.uploadFile(applicationId, questionId, file))
            );
          }
        });
      }

      if (sublistFiles) {
        Object.entries(sublistFiles).forEach(([referenceKey, sublistAnswer]) => {
          const questionId = sublistAnswer?.questionId;
          const sublistQuestionId = sublistAnswer?.sublistQuestionId;
          const files = sublistAnswer?.files;

          if (files?.length) {
            files.forEach((file: File) =>
              uploadPromiseArray.push(
                ApplicationsService.uploadFile(applicationId, questionId, file, sublistQuestionId, referenceKey)
              )
            );
          }
        });
      }

      if (!uploadPromiseArray.length) {
        request(answersData);
        return;
      }

      Promise.all(uploadPromiseArray)
        .then((data: ApplicationUploadedFile[]) => {
          data.forEach((item: ApplicationUploadedFile) => {
            const fileQuestionId: string = getQuestionId(item.questionId);
            const answerIndex = result.findIndex(({ questionId }) => questionId === fileQuestionId);

            if (answerIndex >= 0) {
              if (item.referenceKey && item.sublistQuestionId) {
                const sublistAnswerSet: any = result[answerIndex]?.sublistAnswers || [];
                let setIndex = -1;
                let sublistAnswerIndex = -1;
                sublistAnswerSet.forEach((answerSet: any, outerIndex: number) =>
                  answerSet.forEach(({ sublistQuestionId, referenceKey }: SublistAnswerData, innerIndex: number) => {
                    if (sublistQuestionId === item.sublistQuestionId && referenceKey === item.referenceKey) {
                      setIndex = outerIndex;
                      sublistAnswerIndex = innerIndex;
                    }
                  })
                );

                if (setIndex >= 0 && sublistAnswerIndex >= 0) {
                  const existing = sublistAnswerSet[setIndex][sublistAnswerIndex] || [];
                  if (existing) existing.files = [...(existing.files || []), { id: item.fileId }];
                }
              } else result[answerIndex].files = [...(result[answerIndex].files || []), { id: item.fileId }];
            }
          });

          request(result);
        })
        .catch((errorMessage: string) => {
          setLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [applicationId, enqueueSnackbar]
  );

  const handleFormSave = useCallback(() => {
    setLoading(true);
    const formValues = getValues();
    const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);
    const files: ApplicationFileAnswer = formValues['files'];
    const sublistFiles: ApplicationFileAnswer = formValues['sublistFiles'];

    uploadFiles(
      (data: ApplicationAnswerData[]) =>
        ApplicationsService.updateApplicationContent(applicationId, data)
          .then(() => {
            setLoading(false);
            removeUnsavedChangesPrompt();
            enqueueSnackbar('Application successfully saved', { variant: 'success' });
            refetch();
          })
          .catch((error: string) => {
            setLoading(false);
            enqueueSnackbar(error, defaultSnackbarErrorProps);
          }),
      answersData,
      files,
      sublistFiles
    );
  }, [applicationId, enqueueSnackbar, getValues, uploadFiles, questionsList, removeUnsavedChangesPrompt, refetch]);

  const handleFormSubmit = useCallback(
    (formValues: any) => {
      setLoading(true);
      const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);
      const files: ApplicationFileAnswer = formValues['files'];
      const sublistFiles: ApplicationFileAnswer = formValues['sublistFiles'];

      uploadFiles(
        (data: ApplicationAnswerData[]) =>
          ApplicationsService.submitApplicationContent(applicationId, data)
            .then((result: ApplicationSubmitResponseData) => {
              const { isOk, messages = [] } = result;

              setLoading(false);
              setSubmitResult(result);
              if (isOk) {
                removeUnsavedChangesPrompt();
              } else if (messages.length) {
                messages.forEach(({ questionId, message }) => {
                  setError(getQuestionFieldName(questionId), { type: 'required', message });
                });
              }
              enqueueSnackbar('Application successfully submitted', { variant: 'success' });
            })
            .catch((error: string) => {
              setLoading(false);
              enqueueSnackbar(error, defaultSnackbarErrorProps);
            }),
        answersData,
        files,
        sublistFiles
      );
    },
    [questionsList, uploadFiles, applicationId, enqueueSnackbar, removeUnsavedChangesPrompt, setError]
  );

  const handleCancel = useCallback(() => {
    if (data.createdById && data.applicantId !== data.createdById) {
      history.push(routes.companyApplications);
    } else {
      history.push(routes.applications);
    }
  }, [data, history]);

  return (
    <>
      <ApplicationPageLayout
        data={data}
        footerProps={
          isPrivate
            ? {
                onSubmit: handleFormSave,
                submitButtonName: 'Save',
                submitButtonDisabled: !hasContent || loading,
              }
            : {
                onSubmit: handleSubmit(handleFormSubmit),
                submitButtonName: 'Submit',
                submitButtonDisabled,
                children: (
                  <>
                    <Button
                      color={'secondary'}
                      variant={'outlined'}
                      onClick={handleCancel}
                      className={pageStyles.cancelButton}
                      disabled={loading}
                    >
                      {'Go Back'}
                    </Button>
                    {data.decisionDate && (
                      <Button color={'secondary'} variant={'outlined'} onClick={handleDecisionModalOpen}>
                        {'View Decision'}
                      </Button>
                    )}
                    <Button
                      color={'secondary'}
                      variant={'outlined'}
                      onClick={handleFormSave}
                      disabled={!hasContent || loading}
                      className={pageStyles.saveButton}
                    >
                      {'Save'}
                    </Button>
                  </>
                ),
              }
        }
      >
        <Spinner loading={loading}>
          {data && (
            <>
              <div className={pageStyles.statusAlert}>{getApplicationStatusAlert(data)}</div>
              <div className={pageStyles.questionsWrapper}>
                {data.title && (
                  <h1 className={commonStyles.pageTitle}>
                    {data.campaignName ? `${data.title} (${data.campaignName})` : data.title}
                    {` (ID# ${data.id})`}
                  </h1>
                )}
                {hasContent ? (
                  <ApplicationQuestionsFormSection sections={sections} />
                ) : (
                  <Alert severity={'error'} className={commonStyles.alert}>
                    {'No content found'}
                  </Alert>
                )}
              </div>
            </>
          )}
        </Spinner>
      </ApplicationPageLayout>
      {submitResult && (
        <ApplicationConfirmationModal
          open={submitResult.isOk}
          showPaymentLink={getFormattedApplicationStatus(submitResult.status) === ApplicationStatus.PendingPayment}
          applicationsUrl={
            data.createdById && data.applicantId !== data.createdById ? routes.companyApplications : routes.applications
          }
        />
      )}
      {prompt}
      {data.decisionDate && (
        <ApplicationDecisionModal onClose={handleDecisionModalClose} open={decisionModalOpen} application={data} />
      )}
    </>
  );
};
export default EditApplicationPageView;
